import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, IconButton, CircularProgress,
  Grid, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Avatar, Divider, Alert, Button,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, TextField, MenuItem, InputAdornment, TablePagination,
} from '@mui/material';
import {
  ArrowBack, Edit, Phone, LocationOn, Delete, PhotoCamera,
  Timeline, BarChart, Close, Refresh, UploadFile, Call,
  Contacts as ContactsIcon, PhotoLibrary, DirectionsCar, Search,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { InvokeApi, postImg, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const InfoRow = ({ label, value }) => (
  <Box sx={{ py: 1, borderBottom: '1px solid #f5f5f5' }}>
    <Typography fontSize={11} color="text.secondary" fontWeight={500}>{label}</Typography>
    <Typography fontSize={13} fontWeight={600} color={value && value !== '—' ? '#1a1a1a' : 'text.disabled'}>{value || '—'}</Typography>
  </Box>
);

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  // Core state
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('User');
  const [user, setUser] = useState(null);
  const [gpsData, setGpsData] = useState(null);

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  // Edit form state
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Per-tab data
  const [callRecords, setCallRecords] = useState([]);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callSearch, setCallSearch] = useState('');
  const [callPage, setCallPage] = useState(0);
  const [callPageSize, setCallPageSize] = useState(10);

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactPage, setContactPage] = useState(0);
  const [contactPageSize, setContactPageSize] = useState(10);

  // Photos state
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);

  // GPS Map dialog
  const [mapDialog, setMapDialog] = useState(false);

  // Vehicle management state
  const [returnDialog, setReturnDialog] = useState(false);
  const [returnNotes, setReturnNotes] = useState('');
  const [returningVehicle, setReturningVehicle] = useState(false);
  
  const [changeDialog, setChangeDialog] = useState(false);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [selectedNewVehicle, setSelectedNewVehicle] = useState('');
  const [changingVehicle, setChangingVehicle] = useState(false);

  useEffect(() => {
    loadUserData();
  }, [userId]);

  const loadUserData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Fetch user details
      const uRes = await InvokeApi(Config.base_url + apiList.getUser, { id: userId });
      if (uRes?.code === '200' && uRes.user) {
        const u = uRes.user;
        setUser(u);
        // Initialize form with user data
        setForm({
          id: u.id,
          name: u.name || '',
          phone: u.phone || '',
          email: u.email || '',
          address: u.address || '',
          kyc_status: u.kyc_status || 'pending',
          is_active: u.is_active !== undefined ? u.is_active : 1,
        });
      }

      // Fetch GPS data (using user ID as device_id)
      const gRes = await InvokeApi(Config.base_url + '/getDeviceLocations', { device_id: String(userId) });
      if (gRes?.code === '200' && gRes.location) {
        setGpsData(gRes.location);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load call logs when Call Logs tab is active (combines both admin and device calls)
  useEffect(() => {
    if (activeTab !== 'CallLogs') return;
    const loadCalls = async () => {
      setCallsLoading(true);
      // Get device call logs
      const data = await InvokeApi(Config.base_url + '/getDeviceCallLogs', { device_id: String(userId) });
      setCallRecords(data?.call_logs || []);
      setCallsLoading(false);
    };
    loadCalls();
  }, [activeTab, userId]);

  // Load contacts when Contacts tab is active
  useEffect(() => {
    if (activeTab !== 'Contacts') return;
    const loadContacts = async () => {
      setContactsLoading(true);
      const data = await InvokeApi(Config.base_url + '/getDeviceContacts', { device_id: String(userId) });
      setContacts(data?.contacts || []);
      setContactsLoading(false);
    };
    loadContacts();
  }, [activeTab, userId]);

  // Load photos when Photos tab is active
  useEffect(() => {
    if (activeTab !== 'Photos') return;
    const loadPhotos = async () => {
      setPhotosLoading(true);
      const data = await InvokeApi(Config.base_url + '/getDevicePhotos', { device_id: String(userId) });
      setPhotos(data?.photos || []);
      setPhotosLoading(false);
    };
    loadPhotos();
  }, [activeTab, userId]);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('id', userId);
      
      const res = await postImg(Config.base_url + apiList.uploadUserImage, formData);
      if (res?.code === '200') {
        setSnack({ open: true, msg: 'Image uploaded successfully', sev: 'success' });
        await loadUserData();
      } else {
        setSnack({ open: true, msg: res?.message || 'Upload failed', sev: 'error' });
      }
    } catch (err) {
      setSnack({ open: true, msg: 'Upload error: ' + err.message, sev: 'error' });
    } finally {
      setUploadingImage(false);
    }
  };

  // Edit form handlers
  const handleSave = async () => {
    if (!form.name?.trim()) {
      setSnack({ open: true, msg: 'Name is required', sev: 'error' });
      return;
    }
    if (!form.phone?.trim()) {
      setSnack({ open: true, msg: 'Phone is required', sev: 'error' });
      return;
    }

    setSaving(true);
    const data = await InvokeApi(Config.base_url + apiList.editUser, form);
    setSaving(false);
    
    if (data?.code === '200') {
      setSnack({ open: true, msg: 'User updated successfully', sev: 'success' });
      await loadUserData();
      setActiveTab('User');
    } else {
      setSnack({ open: true, msg: data?.message || 'Failed to update', sev: 'error' });
    }
  };

  const f = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleDelete = async () => {
    if (!window.confirm('Delete this user?')) return;
    const data = await InvokeApi(Config.base_url + apiList.deleteUser, { id: userId });
    if (data?.code === '200') navigate('/users');
    else alert(data?.message || 'Failed to delete');
  };

  // Open GPS Map Dialog
  const openMapDialog = () => {
    setMapDialog(true);
  };

  // Return Vehicle Handler
  const handleReturnVehicle = async () => {
    if (!user.vehicle?.vehicle_id) return;
    
    setReturningVehicle(true);
    const data = await InvokeApi(Config.base_url + '/admin/vehicle/return', {
      user_id: parseInt(userId),
      vehicle_id: user.vehicle.vehicle_id,
      notes: returnNotes.trim() || undefined,
    });
    setReturningVehicle(false);

    if (data?.code === '200') {
      setSnack({ open: true, msg: 'Vehicle returned successfully', sev: 'success' });
      setReturnDialog(false);
      setReturnNotes('');
      await loadUserData();
    } else {
      setSnack({ open: true, msg: data?.message || 'Failed to return vehicle', sev: 'error' });
    }
  };

  // Open Change Vehicle Dialog
  const openChangeVehicleDialog = async () => {
    setChangeDialog(true);
    // Fetch available vehicles
    const res = await InvokeApi(Config.base_url + apiList.getUnassignedVehicles, {});
    if (res?.code === '200') {
      setAvailableVehicles(res.vehicles || []);
    }
  };

  // Change Vehicle Handler
  const handleChangeVehicle = async () => {
    if (!selectedNewVehicle) {
      setSnack({ open: true, msg: 'Please select a vehicle', sev: 'error' });
      return;
    }
    if (!user.vehicle?.vehicle_id) return;

    setChangingVehicle(true);
    
    // Step 1: Return current vehicle
    const returnRes = await InvokeApi(Config.base_url + '/admin/vehicle/return', {
      user_id: parseInt(userId),
      vehicle_id: user.vehicle.vehicle_id,
      notes: 'Vehicle changed',
    });

    if (returnRes?.code !== '200') {
      setChangingVehicle(false);
      setSnack({ open: true, msg: 'Failed to return current vehicle', sev: 'error' });
      return;
    }

    // Step 2: Assign new vehicle
    const assignRes = await InvokeApi(Config.base_url + apiList.assignVehicle, {
      user_id: parseInt(userId),
      vehicle_id: parseInt(selectedNewVehicle),
    });

    setChangingVehicle(false);

    if (assignRes?.code === '200' || assignRes?.status === 'success') {
      setSnack({ open: true, msg: 'Vehicle changed successfully', sev: 'success' });
      setChangeDialog(false);
      setSelectedNewVehicle('');
      await loadUserData();
    } else {
      setSnack({ open: true, msg: assignRes?.message || 'Failed to assign new vehicle', sev: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress sx={{ color: '#1a237e' }} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="text.secondary">User not found</Typography>
        <Button onClick={() => navigate('/users')} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const kycStatusColor = {
    pending: { bg: '#fff3e0', color: '#e65100' },
    approved: { bg: '#e8f5e9', color: '#2e7d32' },
    rejected: { bg: '#ffebee', color: '#c62828' },
    review: { bg: '#e3f2fd', color: '#0277bd' },
  };

  const TABS = [
    { id: 'User', label: 'User Information' },
    { id: 'CallLogs', label: 'Call Logs' },
    { id: 'Contacts', label: 'Contacts' },
    { id: 'Photos', label: 'Photos' },
    { id: 'Edit', label: 'Edit' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Tooltip title="Back">
          <IconButton onClick={() => navigate('/users')} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
            <ArrowBack sx={{ color: '#1a237e' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={800} color="#1a237e">{user.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            ID: #{user.id} · {user.phone}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        
        {/* Left Sidebar (3 cols) - Sticky */}
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', position: 'sticky', top: 80 }}>
            {/* Header gradient */}
            <Box sx={{ height: 70, background: 'linear-gradient(135deg,#1a237e,#1565c0)' }} />
            
            <CardContent sx={{ pt: 0, textAlign: 'center', pb: 3 }}>
              {/* User Image with upload button */}
              <Box sx={{ position: 'relative', display: 'inline-block', mt: -5 }}>
                <Avatar
                  src={user.photo_url ? `${user.photo_url}` : undefined}
                  sx={{
                    width: 80,
                    height: 80,
                    border: '3px solid #fff',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                    fontSize: 28,
                    fontWeight: 900,
                    color: '#fff',
                  }}
                >
                  {!user.photo_url && (user.name?.[0]?.toUpperCase() || 'U')}
                </Avatar>
                
                <label htmlFor="user-image-upload">
                  <input
                    id="user-image-upload"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                  <Tooltip title="Change User Photo">
                    <IconButton
                      component="span"
                      size="small"
                      disabled={uploadingImage}
                      sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: -2,
                        background: '#1a237e',
                        color: '#fff',
                        width: 26,
                        height: 26,
                        '&:hover': { background: '#1565c0' },
                      }}
                    >
                      {uploadingImage ? <CircularProgress size={12} color="inherit" /> : <PhotoCamera sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Tooltip>
                </label>
              </Box>

              <Typography fontWeight={800} fontSize={15} mt={1.5}>{user.name}</Typography>
              <Typography fontSize={12} color="text.secondary">{user.phone}</Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                <Chip label={`#${user.id}`} size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 700, fontSize: 10 }} />
                <Chip
                  label={user.kyc_status?.toUpperCase() || 'PENDING'}
                  size="small"
                  sx={{
                    background: kycStatusColor[user.kyc_status]?.bg || '#fff3e0',
                    color: kycStatusColor[user.kyc_status]?.color || '#e65100',
                    fontWeight: 600,
                    fontSize: 10,
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Quick Info Icons */}
              <Box sx={{ textAlign: 'left', px: 1 }}>
                {[
                  { icon: <Phone fontSize="small" />, value: user.phone },
                  { icon: <LocationOn fontSize="small" />, value: user.address },
                ].map((r, i) => r.value ? (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
                    <Box sx={{ color: '#1a237e', flexShrink: 0 }}>{r.icon}</Box>
                    <Typography fontSize={12} color="text.secondary" sx={{ wordBreak: 'break-all' }}>{r.value}</Typography>
                  </Box>
                ) : null)}
              </Box>

              {/* Assigned Vehicle Info */}
              {user.vehicle && user.vehicle.vehicle_number && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box 
                    onClick={() => navigate(`/vehicle/${user.vehicle.vehicle_id}`)}
                    sx={{ 
                      p: 2, 
                      background: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)', 
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        background: 'linear-gradient(135deg,#c8e6c9,#a5d6a7)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(76,175,80,0.3)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: '#2e7d32', 
                          fontSize: 14, 
                          fontWeight: 700 
                        }}
                      >
                        {user.vehicle.vehicle_number[0]}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: 9, textTransform: 'uppercase' }}>
                          ASSIGNED VEHICLE
                        </Typography>
                        <Typography variant="body2" fontWeight={800} color="#2e7d32" fontSize={13}>
                          {user.vehicle.vehicle_number}
                        </Typography>
                      </Box>
                      <ArrowBack sx={{ fontSize: 16, color: '#2e7d32', transform: 'rotate(180deg)' }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" fontSize={10}>
                      Assigned: {formatDate(user.vehicle.assigned_date)}
                    </Typography>
                  </Box>

                  {/* Vehicle Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); setReturnDialog(true); }}
                      sx={{ 
                        borderColor: '#ff9800', 
                        color: '#ff9800', 
                        fontSize: 11, 
                        fontWeight: 600,
                        '&:hover': { background: '#fff3e0', borderColor: '#ff9800' } 
                      }}
                    >
                      Return Vehicle
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={(e) => { e.stopPropagation(); openChangeVehicleDialog(); }}
                      sx={{ 
                        borderColor: '#2196f3', 
                        color: '#2196f3', 
                        fontSize: 11, 
                        fontWeight: 600,
                        '&:hover': { background: '#e3f2fd', borderColor: '#2196f3' } 
                      }}
                    >
                      Change Vehicle
                    </Button>
                  </Box>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Action Buttons */}
              <Button
                fullWidth
                variant="contained"
                startIcon={<LocationOn />}
                onClick={openMapDialog}
                sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#2e7d32,#388e3c)', fontSize: 12, fontWeight: 600, mb: 1 }}
              >
                View on Map
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setActiveTab('Edit')}
                sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e', fontSize: 12, fontWeight: 600, mb: 1, '&:hover': { background: '#e8eaf6' } }}
              >
                Edit User
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Delete />}
                onClick={handleDelete}
                sx={{ borderRadius: 2, color: '#c62828', borderColor: '#c62828', fontSize: 12, fontWeight: 600, '&:hover': { background: '#ffebee' } }}
              >
                Delete User
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Content (9 cols) */}
        <Grid item xs={12} md={9}>
          
          {/* Pill-style Tabs */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {TABS.map((t) => (
              <Box
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  px: 2,
                  py: 1,
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: activeTab === t.id ? '#1a237e' : '#e8eaf6',
                  background: activeTab === t.id ? '#e8eaf6' : '#fff',
                  transition: 'all 0.2s',
                }}
              >
                <Typography fontSize={13} fontWeight={700} color={activeTab === t.id ? '#1a237e' : 'text.secondary'}>
                  {t.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Tab Content */}
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              
              {/* User Information Tab */}
              {activeTab === 'User' && (
                <>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>User Information</Typography>
                  <Grid container spacing={0}>
                    {[
                      { label: 'Full Name', value: user.name },
                      { label: 'Phone', value: user.phone },
                      { label: 'Email', value: user.email },
                      { label: 'Address', value: user.address },
                      { label: 'KYC Status', value: user.kyc_status?.toUpperCase() },
                      { label: 'Status', value: user.is_active ? 'Active' : 'Inactive' },
                      { label: 'Registered', value: formatDate(user.created_at) },
                      { label: 'Last Updated', value: formatDate(user.updated_at) },
                    ].map(({ label, value }) => (
                      <Grid item xs={12} sm={6} key={label} sx={{ px: 1 }}>
                        <InfoRow label={label} value={value} />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {/* Call Logs Tab - Combined with Search */}
              {activeTab === 'CallLogs' && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Call Logs</Typography>
                    <TextField
                      size="small"
                      placeholder="Search number or name..."
                      value={callSearch}
                      onChange={(e) => {
                        setCallSearch(e.target.value);
                        setCallPage(0);
                      }}
                      sx={{ minWidth: 250 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  
                  {callsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : (() => {
                    const filteredCalls = callRecords.filter(call =>
                      !callSearch ||
                      call.number?.toLowerCase().includes(callSearch.toLowerCase()) ||
                      call.name?.toLowerCase().includes(callSearch.toLowerCase())
                    );
                    
                    const paginatedCalls = filteredCalls.slice(
                      callPage * callPageSize,
                      callPage * callPageSize + callPageSize
                    );
                    
                    return filteredCalls.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 8, background: '#f8fafc', borderRadius: 2 }}>
                        <Call sx={{ fontSize: 56, color: '#e8eaf6', mb: 1 }} />
                        <Typography color="text.secondary" fontWeight={600}>
                          {callSearch ? 'No matching call logs found' : 'No call logs found'}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Showing {callPage * callPageSize + 1}-{Math.min((callPage + 1) * callPageSize, filteredCalls.length)} of {filteredCalls.length} call logs
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: '#1a237e' }}>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Number</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Duration</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Date & Time</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginatedCalls.map((call, i) => (
                                <TableRow key={i} hover>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{call.number || '—'}</TableCell>
                                  <TableCell sx={{ fontSize: 12 }}>{call.name || '—'}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={call.type || '—'} 
                                      size="small" 
                                      sx={{ 
                                        fontSize: 10, 
                                        background: call.type === 'incoming' || call.type === 'INCOMING' ? '#e3f2fd' : 
                                                   call.type === 'outgoing' || call.type === 'OUTGOING' ? '#e8f5e9' : '#ffebee',
                                        color: call.type === 'incoming' || call.type === 'INCOMING' ? '#0277bd' : 
                                               call.type === 'outgoing' || call.type === 'OUTGOING' ? '#2e7d32' : '#c62828',
                                        fontWeight: 600
                                      }} 
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 12 }}>
                                    {call.duration ? (
                                      typeof call.duration === 'number' && call.duration > 60 
                                        ? `${Math.floor(call.duration / 60)}m ${call.duration % 60}s` 
                                        : `${call.duration}s`
                                    ) : '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 12 }}>
                                    {call.date || '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={filteredCalls.length}
                          page={callPage}
                          onPageChange={(_, newPage) => setCallPage(newPage)}
                          rowsPerPage={callPageSize}
                          onRowsPerPageChange={(e) => {
                            setCallPageSize(parseInt(e.target.value, 10));
                            setCallPage(0);
                          }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          sx={{ borderTop: '1px solid #e8eaf6' }}
                        />
                      </>
                    );
                  })()}
                </>
              )}

              {/* Contacts Tab - Table with Search */}
              {activeTab === 'Contacts' && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Contacts</Typography>
                    <TextField
                      size="small"
                      placeholder="Search name or phone..."
                      value={contactSearch}
                      onChange={(e) => {
                        setContactSearch(e.target.value);
                        setContactPage(0);
                      }}
                      sx={{ minWidth: 250 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                  
                  {contactsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : (() => {
                    const filteredContacts = contacts.filter(contact =>
                      !contactSearch ||
                      contact.name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
                      (contact.phones ?? []).some(p => p.includes(contactSearch))
                    );
                    
                    const paginatedContacts = filteredContacts.slice(
                      contactPage * contactPageSize,
                      contactPage * contactPageSize + contactPageSize
                    );
                    
                    return filteredContacts.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 8, background: '#f8fafc', borderRadius: 2 }}>
                        <ContactsIcon sx={{ fontSize: 56, color: '#e8eaf6', mb: 1 }} />
                        <Typography color="text.secondary" fontWeight={600}>
                          {contactSearch ? 'No matching contacts found' : 'No contacts found'}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Showing {contactPage * contactPageSize + 1}-{Math.min((contactPage + 1) * contactPageSize, filteredContacts.length)} of {filteredContacts.length} contacts
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ background: '#1a237e' }}>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Phone Numbers</TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#fff' }}>Emails</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paginatedContacts.map((contact, i) => (
                                <TableRow key={i} hover>
                                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{contact.name || '—'}</TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                                    {(contact.phones ?? []).length > 0 ? (
                                      contact.phones.map((phone, idx) => (
                                        <Box key={idx} component="span" sx={{ display: 'block' }}>{phone}</Box>
                                      ))
                                    ) : '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 11 }}>
                                    {(contact.emails ?? []).length > 0 ? (
                                      contact.emails.map((email, idx) => (
                                        <Box key={idx} component="span" sx={{ display: 'block' }}>{email}</Box>
                                      ))
                                    ) : '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <TablePagination
                          component="div"
                          count={filteredContacts.length}
                          page={contactPage}
                          onPageChange={(_, newPage) => setContactPage(newPage)}
                          rowsPerPage={contactPageSize}
                          onRowsPerPageChange={(e) => {
                            setContactPageSize(parseInt(e.target.value, 10));
                            setContactPage(0);
                          }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          sx={{ borderTop: '1px solid #e8eaf6' }}
                        />
                      </>
                    );
                  })()}
                </>
              )}

              {/* Photos Tab */}
              {activeTab === 'Photos' && (
                <>
                  <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>Photos</Typography>
                  
                  {photosLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress sx={{ color: '#1a237e' }} />
                    </Box>
                  ) : photos.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, background: '#f8fafc', borderRadius: 2 }}>
                      <PhotoLibrary sx={{ fontSize: 56, color: '#e8eaf6', mb: 1 }} />
                      <Typography color="text.secondary" fontWeight={600}>No photos found</Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {photos.map((photo, i) => (
                        <Grid item xs={6} sm={4} md={3} key={i}>
                          <Box
                            component="img"
                            src={`${Config.base_url}/uploads/device-photos/${userId?.replace(/[^a-zA-Z0-9_-]/g, '_')}/${photo.filename}`}
                            alt={`Photo ${i + 1}`}
                            sx={{
                              width: '100%',
                              height: 150,
                              objectFit: 'cover',
                              borderRadius: 2,
                              cursor: 'pointer',
                              '&:hover': { transform: 'scale(1.05)', transition: 'all 0.2s' }
                            }}
                            onClick={() => window.open(`${Config.base_url}/uploads/device-photos/${userId?.replace(/[^a-zA-Z0-9_-]/g, '_')}/${photo.filename}`, '_blank')}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </>
              )}

              {/* Edit Tab */}
              {activeTab === 'Edit' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight={700} color="#1a237e">Edit User</Typography>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={handleSave}
                      disabled={saving}
                      sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 12, fontWeight: 600 }}
                    >
                      {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
                    </Button>
                  </Box>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Name *" value={form.name} onChange={f('name')} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Phone *" value={form.phone} onChange={f('phone')} required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" label="Email" type="email" value={form.email} onChange={f('email')} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth size="small" select label="KYC Status" value={form.kyc_status} onChange={f('kyc_status')}>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                        <MenuItem value="review">In Review</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Address" value={form.address} onChange={f('address')} multiline rows={2} />
                    </Grid>
                  </Grid>
                </>
              )}

            </CardContent>
          </Card>

        </Grid>

      </Grid>

      {/* GPS Map Dialog */}
      <Dialog open={mapDialog} onClose={() => setMapDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn />
            <span>User Location</span>
          </Box>
          <IconButton size="small" onClick={() => setMapDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ height: 480 }}>
            {gpsData?.latitude ? (
              <MapContainer
                center={[gpsData.latitude, gpsData.longitude]}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                <Marker position={[gpsData.latitude, gpsData.longitude]}>
                  <Popup>
                    <strong>{user.name}</strong><br />
                    {parseFloat(gpsData.latitude)?.toFixed(6)}, {parseFloat(gpsData.longitude)?.toFixed(6)}
                  </Popup>
                </Marker>
              </MapContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f8fafc' }}>
                <Typography color="text.secondary">No GPS location available</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setMapDialog(false)} sx={{ fontWeight: 700 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Return Vehicle Dialog */}
      <Dialog open={returnDialog} onClose={() => setReturnDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', pb: 1 }}>
          Return Vehicle
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Return <strong>{user.vehicle?.vehicle_number}</strong> from <strong>{user.name}</strong>?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Return Notes (Optional)"
            placeholder="Enter any notes about the vehicle condition or return details..."
            value={returnNotes}
            onChange={(e) => setReturnNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReturnDialog(false)} disabled={returningVehicle}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReturnVehicle}
            disabled={returningVehicle}
            sx={{ background: 'linear-gradient(135deg,#ff9800,#f57c00)', fontWeight: 600 }}
          >
            {returningVehicle ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Return Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Vehicle Dialog */}
      <Dialog open={changeDialog} onClose={() => setChangeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', pb: 1 }}>
          Change Vehicle
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Current: <strong>{user.vehicle?.vehicle_number}</strong>
          </Typography>
          <Typography variant="body2" fontWeight={600} mb={1}>
            Select New Vehicle:
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={selectedNewVehicle}
            onChange={(e) => setSelectedNewVehicle(e.target.value)}
            label="Available Vehicles"
            helperText={availableVehicles.length === 0 ? 'No unassigned vehicles available' : `${availableVehicles.length} vehicles available`}
          >
            {availableVehicles.length === 0 ? (
              <MenuItem disabled>No vehicles available</MenuItem>
            ) : (
              availableVehicles.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.vehicle_number} - {v.model} ({v.color})
                </MenuItem>
              ))
            )}
          </TextField>
          <Alert severity="info" sx={{ mt: 2, fontSize: 12 }}>
            This will return the current vehicle and assign the new one in a single operation.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setChangeDialog(false)} disabled={changingVehicle}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeVehicle}
            disabled={changingVehicle || !selectedNewVehicle}
            sx={{ background: 'linear-gradient(135deg,#2196f3,#1976d2)', fontWeight: 600 }}
          >
            {changingVehicle ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Change Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.sev} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default UserDetail;
